import * as React from "react";
import { Loader2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/*
 * Canonical confirm dialog for delete / destructive actions.
 *
 * Pure shadcn — composed on top of `src/components/ui/dialog.tsx`. Replaces
 * the MUI `BaseDialog` pattern used today for soft-deletes ("Do you want to
 * delete X?"). Same-tier z-index as the shadcn Dialog primitive, so stacks
 * naturally above any open EntitySheet via DOM order.
 *
 * Usage:
 *
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title={t('resource.delete.title', { name: row.name })}
 *     description={t('resource.delete.warning')}
 *     confirmLabel={t('common.delete')}
 *     onConfirm={async () => { await deleteResource(id).unwrap(); }}
 *     isPending={isDeleting}
 *   />
 */

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Defaults to t('common.confirm') / 'Confirm'. */
  confirmLabel?: React.ReactNode;
  /** Defaults to t('common.cancel') / 'Cancel'. */
  cancelLabel?: React.ReactNode;
  /** Awaited; closes the dialog on success unless `keepOpenOnConfirm` is set. */
  onConfirm: () => void | Promise<void>;
  /** Renders a spinner inside the confirm button and disables both buttons. */
  isPending?: boolean;
  /**
   * Styles the confirm button as destructive (red). Default `true` — this
   * component is mostly used for deletes; pass `false` for benign confirms.
   */
  destructive?: boolean;
  /**
   * When true, the dialog stays open after `onConfirm` resolves. Useful when
   * the caller has its own follow-up (e.g. multi-step wizards). Default
   * false — the dialog closes itself once the async work settles.
   */
  keepOpenOnConfirm?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  isPending = false,
  destructive = true,
  keepOpenOnConfirm = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  async function handleConfirm() {
    try {
      await onConfirm();
      if (!keepOpenOnConfirm) onOpenChange(false);
    } catch {
      // Caller is expected to surface its own error toast. We leave the
      // dialog open so the user can retry without re-navigating.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Disable outside-click + Escape close while the action is in
        // flight. Prevents accidental dismiss while waiting on the network.
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {cancelLabel ?? t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2Icon className="mr-2 size-4 animate-spin" />
            ) : null}
            {confirmLabel ?? t("common.confirm", "Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
